/**
 * Paper Trading Module
 * Manages virtual wallet and executing trades
 */

import { state, saveState } from '../state.js';
import { addHolding, removeHolding, updateHolding } from './portfolio.js';
import { showToast, showError } from '../ui/ui-manager.js';

// Default starting balance
const DEFAULT_BALANCE = 100000;

export function initPaperTrading() {
    // Initialize wallet if not exists
    if (state.paperTrading === undefined) {
        state.paperTrading = {
            balance: DEFAULT_BALANCE,
            history: [] // { date, type: 'BUY'|'SELL', symbol, shares, price, total }
        };
        saveState();
    }

    updateWalletUI();
}

/**
 * Execute a Buy Order
 */
export function buyStock(symbol, shares, price) {
    const totalCost = shares * price;

    if (totalCost > state.paperTrading.balance) {
        showError(`資金不足です。必要額: $${totalCost.toFixed(2)}, 残高: $${state.paperTrading.balance.toFixed(2)}`);
        return false;
    }

    // Deduct balance
    state.paperTrading.balance -= totalCost;

    // Log transaction
    logTransaction('BUY', symbol, shares, price);

    // Add to portfolio (Check if we already own it to average cost?)
    // Existing portfolio.js just adds new entry. Let's merge if exists for better trading exp?
    // The current portfolio.js creates individual 'holdings'. 
    // Ideally we should merge. Let's try to find existing holding for symbol.

    const existingHolding = state.portfolio.find(h => h.symbol === symbol);
    if (existingHolding) {
        // Average Cost Calculation
        const totalShares = existingHolding.shares + parseFloat(shares);
        const totalValue = (existingHolding.shares * existingHolding.avgCost) + totalCost;
        const newAvgCost = totalValue / totalShares;

        updateHolding(existingHolding.id, {
            shares: totalShares,
            avgCost: newAvgCost
        });
    } else {
        addHolding(symbol, shares, price);
    }

    saveState();
    updateWalletUI();
    showToast(`${symbol}を ${shares}株 購入しました ($${price})`, 'success');
    return true;
}

/**
 * Execute a Sell Order
 */
export function sellStock(symbol, shares, price) {
    // Check if we own enough
    const existingHolding = state.portfolio.find(h => h.symbol === symbol);
    if (!existingHolding || existingHolding.shares < shares) {
        showError(`保有株数が不足しています。保有: ${existingHolding ? existingHolding.shares : 0}`);
        return false;
    }

    const totalValue = shares * price;

    // Add to balance
    state.paperTrading.balance += totalValue;

    // Log transaction
    logTransaction('SELL', symbol, shares, price);

    // Update portfolio
    const remainingShares = existingHolding.shares - parseFloat(shares);

    if (remainingShares <= 0) {
        removeHolding(existingHolding.id);
    } else {
        updateHolding(existingHolding.id, {
            shares: remainingShares
        });
    }

    saveState();
    updateWalletUI();
    showToast(`${symbol}を ${shares}株 売却しました ($${price})`, 'success');
    return true;
}

function logTransaction(type, symbol, shares, price) {
    state.paperTrading.history.unshift({
        date: new Date().toISOString(),
        type,
        symbol,
        shares,
        price,
        total: shares * price
    });
}

export function updateWalletUI() {
    const balanceEl = document.getElementById('wallet-balance');
    if (balanceEl) {
        balanceEl.textContent = `$${state.paperTrading.balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
}

export function getBalance() {
    return state.paperTrading.balance;
}
